import { useState, useCallback, useEffect } from 'react';
import { 
  getAllProducts, 
  getAllBundles, 
  getAllModifiers, 
  getAllOptionTypes, 
  getAllSlots, 
  getAllModifierWeights 
} from '../../../services/api';

interface MenuData {
  products: any[];
  bundles: any[];
  modifiers: any[];
  optionTypes: any[];
  slots: any[];
  weights: any[];
}

interface MenuDataCache {
  data: MenuData | null;
  isLoading: boolean;
  error: any;
  isLoaded: boolean;
  fetchMenuData: () => Promise<void>;
  menu: MenuData | null;
}

// Module-level singleton state - shared across ALL component instances
let globalMenuData: MenuData | null = null;
let globalIsLoading = false;
let globalError: any = null;
let globalIsLoaded = false;
let globalFetchPromise: Promise<void> | null = null;

// List of all component instances that need to be notified of state changes
const subscribers: Set<() => void> = new Set();

/**
 * Singleton cache for menu data that persists across component instances.
 * Uses module-level state to ensure data is fetched only once per session.
 * All components using this hook share the same cached data.
 */
export const useMenuDataCache = (): MenuDataCache => {
  // Local state to trigger re-renders when global state changes
  const [, forceUpdate] = useState({});
  
  // Subscribe to global state changes on mount
  useEffect(() => {
    const updateFn = () => forceUpdate({});
    subscribers.add(updateFn);
    
    return () => {
      subscribers.delete(updateFn);
    };
  }, []);

  const fetchMenuData = useCallback(async () => {
    // Return early if already loaded or currently loading
    if (globalIsLoaded && globalMenuData) {
      console.log('useMenuDataCache: Data already loaded, skipping API calls');
      return;
    }

    if (globalIsLoading && globalFetchPromise) {
      console.log('useMenuDataCache: Already loading, waiting for existing promise');
      await globalFetchPromise;
      return;
    }

    console.log('useMenuDataCache: Starting API calls for menu data');
    globalIsLoading = true;
    globalError = null;
    
    // Notify all subscribers that loading started
    subscribers.forEach(fn => fn());

    const fetchPromise = (async () => {
      try {
        const [
          productsData,
          bundlesData,
          modifiersData,
          optionTypesData,
          slotsData,
          weightsData,
        ] = await Promise.all([
          getAllProducts(),
          getAllBundles(),
          getAllModifiers(),
          getAllOptionTypes(),
          getAllSlots(),
          getAllModifierWeights(),
        ]);

        const menuData: MenuData = {
          products: productsData,
          bundles: bundlesData,
          modifiers: modifiersData,
          optionTypes: optionTypesData,
          slots: slotsData,
          weights: weightsData,
        };

        globalMenuData = menuData;
        globalIsLoaded = true;
        console.log('useMenuDataCache: Successfully loaded menu data');
      } catch (err) {
        globalError = err;
        globalMenuData = null;
        console.error('useMenuDataCache: Failed to load menu data', err);
      } finally {
        globalIsLoading = false;
        globalFetchPromise = null;
        
        // Notify all subscribers that loading finished
        subscribers.forEach(fn => fn());
      }
    })();
    
    globalFetchPromise = fetchPromise;
    await fetchPromise;
  }, []);

  return {
    data: globalMenuData,
    isLoading: globalIsLoading,
    error: globalError,
    isLoaded: globalIsLoaded,
    fetchMenuData,
    menu: globalMenuData,
  };
};